import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Plus, Search, Network, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Organization = () => {
  const [units, setUnits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    unit_type: 'department',
    name: '',
    code: '',
    parent_id: '',
  });
  const [error, setError] = useState('');

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editForm, setEditForm] = useState({ company_id: '', unit_type: 'department', name: '', code: '', parent_id: '' });
  const [editError, setEditError] = useState('');

  const unitTypes = [
    { value: 'business_unit', label: 'Business Unit' },
    { value: 'branch', label: 'Branch' },
    { value: 'department', label: 'Department' },
    { value: 'cost_center', label: 'Cost Center' },
    { value: 'location', label: 'Location' },
  ];

  useEffect(() => {
    fetchUnits();
    fetchCompanies();
  }, []);

  const fetchUnits = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/organizational-units`, {
        withCredentials: true,
      });
      setUnits(data);
    } catch (error) {
      console.error('Failed to fetch organizational units:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, {
        withCredentials: true,
      });
      setCompanies(data);
      if (data.length > 0) {
        setFormData((prev) => ({ ...prev, company_id: data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await axios.post(`${API_URL}/organizational-units`, formData, {
        withCredentials: true,
      });
      toast.success('Organizational unit created');
      setShowModal(false);
      setFormData({
        company_id: companies[0]?.id || '',
        unit_type: 'department',
        name: '',
        code: '',
        parent_id: '',
      });
      fetchUnits();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create organizational unit');
    }
  };

  const openEditModal = (unit) => {
    setEditingUnit(unit);
    setEditForm({
      company_id: unit.company_id || '',
      unit_type: unit.unit_type || 'department',
      name: unit.name || '',
      code: unit.code || '',
      parent_id: unit.parent_id || '',
    });
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUnit) return;
    setEditError('');
    try {
      const payload = { ...editForm };
      if (!payload.parent_id) delete payload.parent_id;
      await axios.patch(`${API_URL}/organizational-units/${editingUnit.id}`, payload, { withCredentials: true });
      toast.success('Organizational unit updated');
      setShowEditModal(false);
      setEditingUnit(null);
      fetchUnits();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (unit) => {
    if (!window.confirm(`Permanently DELETE "${unit.name}" (${unit.code})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/organizational-units/${unit.id}`, { withCredentials: true });
      toast.success('Organizational unit deleted');
      fetchUnits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const getUnitTypeBadgeColor = (type) => {
    const colors = {
      business_unit: 'bg-purple-50 text-purple-700 border-purple-200',
      branch: 'bg-blue-50 text-blue-700 border-blue-200',
      department: 'bg-green-50 text-green-700 border-green-200',
      cost_center: 'bg-amber-50 text-amber-700 border-amber-200',
      location: 'bg-pink-50 text-pink-700 border-pink-200',
    };
    return colors[type] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Network className="inline-block w-8 h-8 mr-2 mb-1" />
              Organizational Structure
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Manage business units, departments, branches, and locations
            </p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            data-testid="add-org-unit-button"
            className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Unit
          </Button>
        </div>

        {/* Units Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Unit Name
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : units.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">
                    No organizational units found
                  </td>
                </tr>
              ) : (
                units.map((unit) => {
                  const company = companies.find((c) => c.id === unit.company_id);
                  return (
                    <tr
                      key={unit.id}
                      data-testid={`org-unit-row-${unit.code}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                        {unit.name}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getUnitTypeBadgeColor(
                            unit.unit_type
                          )}`}
                        >
                          {unit.unit_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        <span className="font-mono">{unit.code}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        {company?.name || 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        {new Date(unit.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openEditModal(unit)}
                            data-testid={`edit-org-unit-${unit.code}`}
                            className="text-xs px-2 py-1 rounded-md font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 flex items-center gap-1"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(unit)}
                            data-testid={`delete-org-unit-${unit.code}`}
                            className="text-xs px-2 py-1 rounded-md font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Unit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="add-org-unit-modal">
          <DialogHeader>
            <DialogTitle>Add Organizational Unit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company_id}
                onValueChange={(value) => setFormData({ ...formData, company_id: value })}
              >
                <SelectTrigger data-testid="org-unit-company-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="unit_type">Unit Type</Label>
              <Select
                value={formData.unit_type}
                onValueChange={(value) => setFormData({ ...formData, unit_type: value })}
              >
                <SelectTrigger data-testid="org-unit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="name">Unit Name</Label>
              <Input
                id="name"
                data-testid="org-unit-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="code">Unit Code</Label>
              <Input
                id="code"
                data-testid="org-unit-code-input"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="org-unit-submit-button" className="bg-[#0052FF] hover:bg-[#0043D1]">
                Create Unit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent data-testid="edit-org-unit-modal">
          <DialogHeader>
            <DialogTitle>Edit Organizational Unit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {editError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {editError}
              </div>
            )}
            <div>
              <Label htmlFor="edit-company">Company</Label>
              <Select
                value={editForm.company_id}
                onValueChange={(value) => setEditForm({ ...editForm, company_id: value })}
              >
                <SelectTrigger data-testid="edit-org-unit-company-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-unit-type">Unit Type</Label>
              <Select
                value={editForm.unit_type}
                onValueChange={(value) => setEditForm({ ...editForm, unit_type: value })}
              >
                <SelectTrigger data-testid="edit-org-unit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-name">Unit Name</Label>
              <Input
                id="edit-name"
                data-testid="edit-org-unit-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-code">Unit Code</Label>
              <Input
                id="edit-code"
                data-testid="edit-org-unit-code-input"
                value={editForm.code}
                onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="edit-org-unit-submit-button" className="bg-[#0052FF] hover:bg-[#0043D1]">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Organization;
